import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/lib/auth";
import { Menu, X, LogOut, LayoutDashboard, PackagePlus, PackageMinus,
  Boxes, SplitSquareHorizontal, ScrollText, CheckSquare, FileBarChart,
  MapPin, Package, Truck, Users2, Settings as SettingsIcon, Leaf,
  FlaskConical, Factory, GitBranch, ArrowLeftRight } from "lucide-react";

// Nav is grouped so daily work sits on top and setup stays out of the way.
const sectionsFor = (role) => {
  if (role === "receiver") {
    return [{ title: null, links: [{ to: "/", label: "Receive & Transfers", icon: PackagePlus }] }];
  }
  const sections = [
    {
      title: "Daily",
      links: [
        { to: "/", label: "Dashboard", icon: LayoutDashboard },
        { to: "/stock", label: "Find Stock", icon: Boxes },
        { to: "/map", label: "Room Map", icon: MapPin },
        { to: "/receipt", label: "Receive Stock", icon: PackagePlus },
        { to: "/issue", label: "Issue Stock", icon: PackageMinus },
        { to: "/transfers", label: "Transfers", icon: ArrowLeftRight },
        { to: "/containers", label: "Move & Repack", icon: SplitSquareHorizontal },
        { to: "/arrange", label: "Arrange Racks", icon: MapPin },
      ],
    },
  ];
  if (["admin", "manager", "executive"].includes(role)) {
    sections.push({
      title: "Blending",
      links: [
        ...(["admin", "manager"].includes(role)
          ? [{ to: "/formulations", label: "Formulations", icon: FlaskConical }]
          : []),
        { to: "/production", label: "Production", icon: Factory },
        { to: "/trace", label: "Traceability", icon: GitBranch },
      ],
    });
    sections.push({
      title: "Records",
      links: [
        { to: "/activity", label: "Activity", icon: ScrollText },
        { to: "/approvals", label: "Approvals", icon: CheckSquare },
        { to: "/reports", label: "Reports", icon: FileBarChart },
      ],
    });
  } else {
    sections[0].links.push({ to: "/activity", label: "Activity", icon: ScrollText });
  }
  if (["admin", "manager"].includes(role)) {
    sections.push({
      title: "Setup",
      links: [
        { to: "/admin/items", label: "Items", icon: Package },
        { to: "/admin/locations", label: "Godowns & Racks", icon: MapPin },
        { to: "/admin/suppliers", label: role === "admin" ? "Suppliers" : "Codes", icon: Truck },
        { to: "/admin/import", label: "Import Stock", icon: FileBarChart },
        ...(role === "admin"
          ? [
              { to: "/admin/users", label: "Users", icon: Users2 },
              { to: "/settings", label: "Settings", icon: SettingsIcon },
            ]
          : []),
      ],
    });
  }
  return sections;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const sections = sectionsFor(user?.role || "");

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Top bar (mobile) */}
      <header className="md:hidden flex items-center justify-between px-4 h-14 bg-primary text-primary-foreground border-b border-primary/20 sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <Leaf className="w-5 h-5 text-secondary" strokeWidth={2.5} />
          <span className="font-display font-bold text-lg">ESSENCE</span>
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="p-2"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={`${open ? "block" : "hidden"} md:block fixed md:static top-14 md:top-0 left-0 z-20 w-full md:w-64 h-[calc(100vh-3.5rem)] md:h-screen bg-primary text-primary-foreground overflow-y-auto`}
      >
        <div className="hidden md:flex items-center gap-2 h-16 px-5 border-b border-white/10">
          <Leaf className="w-6 h-6 text-secondary" strokeWidth={2.5} />
          <span className="font-display font-bold text-xl tracking-tight">ESSENCE</span>
        </div>
        <div className="px-5 py-4 border-b border-white/10">
          <div className="data-label text-white/50">Signed in</div>
          <div className="font-semibold mt-1 truncate" data-testid="signed-in-name">{user?.name}</div>
          <div className="badge-flat bg-secondary text-secondary-foreground mt-2">{user?.role}</div>
        </div>
        <nav className="p-2">
          {sections.map((s, si) => (
            <div key={s.title || si} className={si > 0 ? "mt-3" : ""}>
              {s.title && (
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-white/40">
                  {s.title}
                </div>
              )}
              {s.links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-${l.to.replace(/\W+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <l.icon className="w-4 h-4" strokeWidth={2} />
                  {l.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 mt-auto">
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm w-full py-2"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
